/**
 * @module stores/itemStore
 * @description Item state management
 *
 * Manages item state including:
 * - Items list with pagination
 * - Item selection (single and multi)
 * - Sorting and filtering
 * - Search functionality
 * - CRUD operations integration
 *
 * @example
 * import { itemStore, items, selectedItem, loadItems } from '@stores/itemStore';
 *
 * // Load items for a collection
 * await loadItems(libraryId, 'user', { collectionKey: 'ABC123' });
 *
 * // Select an item
 * selectItem(item);
 *
 * // Search items
 * await searchItems('quantum physics');
 */

import { writable, derived, get, type Readable } from 'svelte/store';
import { items as itemsConfig } from '@/config';
import { createLogger } from '@services/loggerService';
import { notify } from '@services/notificationService';
import { indexedDBService } from '@services/indexedDBService';
import { zoteroService } from '@services/zoteroService';
import { createCancellableRequest, isAbortError } from '@/utils';

// =============================================================================
// MEMOIZATION HELPERS
// =============================================================================

/**
 * Shallow compare two values (arrays or objects)
 * Returns true if they are equal
 */
function shallowEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  }

  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) => (a as Record<string, unknown>)[key] === (b as Record<string, unknown>)[key]
  );
}

/**
 * Create a memoized derived store that only emits when values actually change
 * Prevents unnecessary re-renders when derived values are objects/arrays
 */
function memoizedDerived<S, T>(store: Readable<S>, fn: (value: S) => T): Readable<T> {
  let previousValue: T | undefined;

  return derived(store, ($store, set) => {
    const newValue = fn($store);
    if (!shallowEqual(previousValue, newValue)) {
      previousValue = newValue;
      set(newValue);
    }
  });
}
import type { Item, LibraryType, ItemSortColumn, SortDirection, GetItemsOptions } from '@types';

const log = createLogger('item');

// =============================================================================
// REQUEST CONTROLLER
// =============================================================================

/**
 * Cancellable request controller for managing in-flight item requests.
 * Automatically cancels previous requests when a new one is initiated,
 * preventing race conditions.
 */
const itemsRequest = createCancellableRequest();

// =============================================================================
// TYPES
// =============================================================================

/** Item state */
export interface ItemState {
  /** List of items */
  items: Item[];
  /** Total number of items (for pagination) */
  totalItems: number;
  /** Currently selected item */
  selectedItem: Item | null;
  /** Multi-selected item keys (array for serialization) */
  selectedKeys: string[];
  /** Current page (0-indexed) */
  currentPage: number;
  /** Items per page */
  pageSize: number;
  /** Sort column */
  sortColumn: ItemSortColumn;
  /** Sort direction */
  sortDirection: SortDirection;
  /** Search query */
  searchQuery: string;
  /** Whether items are loading */
  isLoading: boolean;
  /** Whether a single item operation is in progress */
  isItemLoading: boolean;
  /** Error message */
  error: string | null;
  /** Library ID items belong to */
  libraryId: number | null;
  /** Library type */
  libraryType: LibraryType | null;
  /** Current collection key (null for special collections) */
  collectionKey: string | null;
}

/** Load items options */
export interface LoadItemsOptions {
  collectionKey?: string | null;
  searchQuery?: string;
  page?: number;
  resetSelection?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_PAGE_SIZE = itemsConfig.pageSize;
const DEFAULT_SORT_COLUMN: ItemSortColumn = itemsConfig.defaultSortColumn;
const DEFAULT_SORT_DIRECTION: SortDirection = itemsConfig.defaultSortDirection;

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: ItemState = {
  items: [],
  totalItems: 0,
  selectedItem: null,
  selectedKeys: [],
  currentPage: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  sortColumn: DEFAULT_SORT_COLUMN,
  sortDirection: DEFAULT_SORT_DIRECTION,
  searchQuery: '',
  isLoading: false,
  isItemLoading: false,
  error: null,
  libraryId: null,
  libraryType: null,
  collectionKey: null,
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<ItemState>(initialState);

/** Exported item store (read-only subscribe) */
export const itemStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** List of items */
export const items = derived(store, ($store) => $store.items);

/** Item count (current page) */
export const itemCount = derived(store, ($store) => $store.items.length);

/** Total item count */
export const totalItems = derived(store, ($store) => $store.totalItems);

/** Currently selected item */
export const selectedItem = derived(store, ($store) => $store.selectedItem);

/** Multi-selected item keys (as Set for efficient lookups) */
export const selectedItemKeys = derived(store, ($store) => new Set($store.selectedKeys));

/** Number of selected items */
export const selectedCount = derived(
  store,
  ($store) => $store.selectedKeys.length + ($store.selectedItem ? 1 : 0)
);

/** Whether multiple items are selected */
export const hasMultiSelection = derived(store, ($store) => $store.selectedKeys.length > 0);

/** All selected items (memoized to prevent unnecessary re-renders) */
export const selectedItems = memoizedDerived(store, ($store) => {
  const selected: Item[] = [];
  if ($store.selectedItem) {
    selected.push($store.selectedItem);
  }
  for (const key of $store.selectedKeys) {
    const item = $store.items.find((i) => i.key === key);
    if (item && item.key !== $store.selectedItem?.key) {
      selected.push(item);
    }
  }
  return selected;
});

/** Current pagination info (memoized to prevent unnecessary re-renders) */
export const pagination = memoizedDerived(store, ($store) => ({
  currentPage: $store.currentPage,
  pageSize: $store.pageSize,
  totalItems: $store.totalItems,
  totalPages: Math.ceil($store.totalItems / $store.pageSize),
  hasNextPage: ($store.currentPage + 1) * $store.pageSize < $store.totalItems,
  hasPrevPage: $store.currentPage > 0,
}));

/** Current sort state (memoized to prevent unnecessary re-renders) */
export const sortState = memoizedDerived(store, ($store) => ({
  column: $store.sortColumn,
  direction: $store.sortDirection,
}));

/** Current search query */
export const searchQuery = derived(store, ($store) => $store.searchQuery);

/** Whether items are loading */
export const isItemsLoading = derived(store, ($store) => $store.isLoading);

/** Whether a single item operation is loading */
export const isItemOperationLoading = derived(store, ($store) => $store.isItemLoading);

/** Item error */
export const itemError = derived(store, ($store) => $store.error);

/** Whether search is active */
export const isSearchActive = derived(store, ($store) => $store.searchQuery.trim().length > 0);

// =============================================================================
// ACTIONS - LOADING
// =============================================================================

/**
 * Load items for a library/collection
 */
export async function loadItems(
  libraryId: number,
  libraryType: LibraryType,
  options: LoadItemsOptions = {}
): Promise<void> {
  const { collectionKey = null, searchQuery = '', page = 0, resetSelection = true } = options;

  log.info('Loading items', { libraryId, collectionKey, searchQuery, page });

  // Get signal from cancellable request (automatically cancels any in-flight request)
  const signal = itemsRequest.getSignal();

  store.update((s) => ({
    ...s,
    isLoading: true,
    error: null,
    libraryId,
    libraryType,
    collectionKey,
    searchQuery,
    currentPage: page,
    ...(resetSelection && {
      selectedItem: null,
      selectedKeys: [],
    }),
  }));

  const state = get(store);

  const apiOptions: GetItemsOptions = {
    collectionKey: collectionKey || undefined,
    q: searchQuery || undefined,
    sort: state.sortColumn,
    direction: state.sortDirection,
    limit: state.pageSize,
    start: page * state.pageSize,
    signal,
  };

  const result = await zoteroService.getItems(libraryId, libraryType, apiOptions);

  // Check if the request was aborted - if so, don't update state
  if (signal.aborted) {
    log.debug('Items request was aborted, ignoring result');
    return;
  }

  if (result.success) {
    store.update((s) => ({
      ...s,
      items: result.data.data,
      totalItems: result.data.totalResults,
      isLoading: false,
    }));
    log.info('Items loaded', {
      count: result.data.data.length,
      total: result.data.totalResults,
    });
  } else {
    // Don't show error for aborted requests
    if (isAbortError(result.error)) {
      log.debug('Items request aborted');
      return;
    }
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: result.error,
    }));
    notify.error(`Failed to load items: ${result.error}`);
    log.error('Failed to load items', { error: result.error });
  }
}

/**
 * Refresh current items
 */
export async function refreshItems(): Promise<void> {
  const state = get(store);
  if (state.libraryId && state.libraryType) {
    await loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: state.searchQuery,
      page: state.currentPage,
      resetSelection: false,
    });
  }
}

/**
 * Load trash items
 */
export async function loadTrashItems(libraryId: number, libraryType: LibraryType): Promise<void> {
  log.info('Loading trash items', { libraryId });

  // Get signal from cancellable request (automatically cancels any in-flight request)
  const signal = itemsRequest.getSignal();

  store.update((s) => ({
    ...s,
    isLoading: true,
    error: null,
    libraryId,
    libraryType,
    collectionKey: null,
    searchQuery: '',
    selectedItem: null,
    selectedKeys: [],
  }));

  const state = get(store);
  const result = await zoteroService.getTrashItems(libraryId, libraryType, {
    limit: state.pageSize,
    start: state.currentPage * state.pageSize,
    signal,
  });

  // Check if the request was aborted - if so, don't update state
  if (signal.aborted) {
    log.debug('Trash items request was aborted, ignoring result');
    return;
  }

  if (result.success) {
    store.update((s) => ({
      ...s,
      items: result.data.data,
      totalItems: result.data.totalResults,
      isLoading: false,
    }));
    log.info('Trash items loaded', { count: result.data.data.length });
  } else {
    // Don't show error for aborted requests
    if (isAbortError(result.error)) {
      log.debug('Trash items request aborted');
      return;
    }
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: result.error,
    }));
  }
}

// =============================================================================
// ACTIONS - SELECTION
// =============================================================================

/**
 * Select a single item
 */
export function selectItem(item: Item | null): void {
  log.debug('Selecting item', { key: item?.key });
  store.update((s) => ({
    ...s,
    selectedItem: item,
    selectedKeys: [], // Clear multi-selection
  }));
}

/**
 * Toggle item in multi-selection
 */
export function toggleItemSelection(key: string): void {
  store.update((s) => {
    const isSelected = s.selectedKeys.includes(key);
    const newKeys = isSelected ? s.selectedKeys.filter((k) => k !== key) : [...s.selectedKeys, key];
    return { ...s, selectedKeys: newKeys };
  });
}

/**
 * Add item to multi-selection
 */
export function addToSelection(key: string): void {
  store.update((s) => {
    if (s.selectedKeys.includes(key)) return s;
    return { ...s, selectedKeys: [...s.selectedKeys, key] };
  });
}

/**
 * Remove item from multi-selection
 */
export function removeFromSelection(key: string): void {
  store.update((s) => ({
    ...s,
    selectedKeys: s.selectedKeys.filter((k) => k !== key),
  }));
}

/**
 * Select all items
 */
export function selectAllItems(): void {
  store.update((s) => ({
    ...s,
    selectedKeys: s.items.map((i) => i.key),
  }));
}

/**
 * Clear all selection
 */
export function clearSelection(): void {
  store.update((s) => ({
    ...s,
    selectedItem: null,
    selectedKeys: [],
  }));
}

/**
 * Select range of items (shift+click)
 */
export function selectRange(fromKey: string, toKey: string): void {
  const state = get(store);
  const fromIndex = state.items.findIndex((i) => i.key === fromKey);
  const toIndex = state.items.findIndex((i) => i.key === toKey);

  if (fromIndex === -1 || toIndex === -1) return;

  const start = Math.min(fromIndex, toIndex);
  const end = Math.max(fromIndex, toIndex);

  store.update((s) => {
    const currentSet = new Set(s.selectedKeys);
    const keysToAdd: string[] = [];
    for (let i = start; i <= end; i++) {
      const key = s.items[i].key;
      if (!currentSet.has(key)) {
        keysToAdd.push(key);
      }
    }
    return { ...s, selectedKeys: [...s.selectedKeys, ...keysToAdd] };
  });
}

// =============================================================================
// ACTIONS - SORTING
// =============================================================================

/**
 * Set sort column and direction
 */
export function setSort(column: ItemSortColumn, direction?: SortDirection): void {
  const state = get(store);

  // Toggle direction if same column
  const newDirection =
    direction ?? (state.sortColumn === column && state.sortDirection === 'asc' ? 'desc' : 'asc');

  log.debug('Setting sort', { column, direction: newDirection });

  store.update((s) => ({
    ...s,
    sortColumn: column,
    sortDirection: newDirection,
  }));

  // Save preference
  indexedDBService.setUIPreferences({
    sortColumn: column,
    sortDirection: newDirection,
  });

  // Reload items with new sort
  if (state.libraryId && state.libraryType) {
    loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: state.searchQuery,
      page: 0, // Reset to first page
    });
  }
}

/**
 * Toggle sort direction
 */
export function toggleSortDirection(): void {
  const state = get(store);
  setSort(state.sortColumn, state.sortDirection === 'asc' ? 'desc' : 'asc');
}

// =============================================================================
// ACTIONS - PAGINATION
// =============================================================================

/**
 * Go to a specific page
 */
export function goToPage(page: number): void {
  const state = get(store);
  const maxPage = Math.ceil(state.totalItems / state.pageSize) - 1;
  const targetPage = Math.max(0, Math.min(page, maxPage));

  if (targetPage !== state.currentPage && state.libraryId && state.libraryType) {
    loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: state.searchQuery,
      page: targetPage,
    });
  }
}

/**
 * Go to next page
 */
export function nextPage(): void {
  const state = get(store);
  goToPage(state.currentPage + 1);
}

/**
 * Go to previous page
 */
export function prevPage(): void {
  const state = get(store);
  goToPage(state.currentPage - 1);
}

/**
 * Go to first page
 */
export function firstPage(): void {
  goToPage(0);
}

/**
 * Go to last page
 */
export function lastPage(): void {
  const state = get(store);
  const maxPage = Math.ceil(state.totalItems / state.pageSize) - 1;
  goToPage(maxPage);
}

/**
 * Load more items (infinite scroll)
 * Appends items to existing list instead of replacing
 */
export async function loadMoreItems(): Promise<void> {
  const state = get(store);

  // Check if we can load more
  const loadedItems = state.items.length;
  const hasMore = loadedItems < state.totalItems;

  if (!hasMore || state.isLoading || !state.libraryId || !state.libraryType) {
    return;
  }

  log.info('Loading more items', { loaded: loadedItems, total: state.totalItems });

  // Get signal from cancellable request
  const signal = itemsRequest.getSignal();

  store.update((s) => ({
    ...s,
    isLoading: true,
    error: null,
  }));

  const apiOptions: GetItemsOptions = {
    collectionKey: state.collectionKey || undefined,
    q: state.searchQuery || undefined,
    sort: state.sortColumn,
    direction: state.sortDirection,
    limit: state.pageSize,
    start: loadedItems, // Start from where we left off
    signal,
  };

  const result = await zoteroService.getItems(state.libraryId, state.libraryType, apiOptions);

  // Check if the request was aborted
  if (signal.aborted) {
    log.debug('Load more request was aborted');
    return;
  }

  if (result.success) {
    const newItemsCount = result.data.data.length;
    const newTotalLoaded = state.items.length + newItemsCount;

    // Use the API's totalResults if available, otherwise estimate
    let newTotalItems = result.data.totalResults;
    if (newTotalItems === 0 || newTotalItems < newTotalLoaded) {
      // If API didn't return total or it's less than what we've loaded,
      // assume there might be more if we got a full page
      newTotalItems = newItemsCount === state.pageSize ? newTotalLoaded + 1 : newTotalLoaded;
    }

    store.update((s) => ({
      ...s,
      items: [...s.items, ...result.data.data], // Append new items
      totalItems: newTotalItems,
      isLoading: false,
    }));
    log.info('More items loaded', {
      newCount: newItemsCount,
      totalLoaded: newTotalLoaded,
      total: newTotalItems,
    });
  } else {
    if (isAbortError(result.error)) {
      log.debug('Load more request aborted');
      return;
    }
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: result.error,
    }));
    notify.error(`Failed to load more items: ${result.error}`);
  }
}

/**
 * Check if there are more items to load
 */
export const hasMoreItems = derived(
  store,
  ($store) => $store.items.length < $store.totalItems && !$store.isLoading
);

/**
 * Set page size
 */
export function setPageSize(size: number): void {
  store.update((s) => ({
    ...s,
    pageSize: size,
    currentPage: 0, // Reset to first page
  }));

  // Reload with new page size
  const state = get(store);
  if (state.libraryId && state.libraryType) {
    loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: state.searchQuery,
    });
  }
}

// =============================================================================
// ACTIONS - SEARCH
// =============================================================================

/**
 * Search items
 */
export async function searchItems(query: string): Promise<void> {
  const trimmed = query.trim();
  const state = get(store);

  if (state.libraryId && state.libraryType) {
    // Add to search history
    if (trimmed) {
      indexedDBService.addToSearchHistory(trimmed);
    }

    await loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: trimmed,
      page: 0,
    });
  }
}

/**
 * Clear search
 */
export async function clearSearch(): Promise<void> {
  const state = get(store);
  if (state.libraryId && state.libraryType) {
    await loadItems(state.libraryId, state.libraryType, {
      collectionKey: state.collectionKey,
      searchQuery: '',
      page: 0,
    });
  }
}

// =============================================================================
// ACTIONS - ITEM UPDATES
// =============================================================================

/**
 * Add an item to the store (after creation)
 */
export function addItem(item: Item): void {
  store.update((s) => ({
    ...s,
    items: [item, ...s.items],
    totalItems: s.totalItems + 1,
  }));
}

/**
 * Update an item in the store
 */
export function updateItemInStore(key: string, data: Partial<Item>): void {
  store.update((s) => ({
    ...s,
    items: s.items.map((i) => (i.key === key ? { ...i, ...data } : i)),
    selectedItem: s.selectedItem?.key === key ? { ...s.selectedItem, ...data } : s.selectedItem,
  }));
}

/**
 * Remove an item from the store
 */
export function removeItem(key: string): void {
  store.update((s) => {
    const itemExists = s.items.some((i) => i.key === key);
    const newItems = s.items.filter((i) => i.key !== key);
    return {
      ...s,
      items: newItems,
      // Only decrement if item was actually in the list, never go below 0
      totalItems: itemExists ? Math.max(0, s.totalItems - 1) : s.totalItems,
      selectedItem: s.selectedItem?.key === key ? null : s.selectedItem,
      selectedKeys: s.selectedKeys.filter((k) => k !== key),
    };
  });
}

/**
 * Remove multiple items from the store
 */
export function removeItems(keys: string[]): void {
  const keySet = new Set(keys);
  store.update((s) => {
    const removedCount = s.items.filter((i) => keySet.has(i.key)).length;
    const newItems = s.items.filter((i) => !keySet.has(i.key));
    return {
      ...s,
      items: newItems,
      // Only decrement by actual removed count, never go below 0
      totalItems: Math.max(0, s.totalItems - removedCount),
      selectedItem: s.selectedItem && keySet.has(s.selectedItem.key) ? null : s.selectedItem,
      selectedKeys: s.selectedKeys.filter((k) => !keySet.has(k)),
    };
  });
}

// =============================================================================
// ACTIONS - MISC
// =============================================================================

/**
 * Clear item error
 */
export function clearItemError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Set item loading state
 */
export function setItemLoading(loading: boolean): void {
  store.update((s) => ({ ...s, isItemLoading: loading }));
}

/**
 * Get current item state
 */
export function getItemState(): ItemState {
  return get(store);
}

/**
 * Get item by key
 */
export function getItemByKey(key: string): Item | null {
  const state = get(store);
  return state.items.find((i) => i.key === key) ?? null;
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state
 */
export function resetItemStore(): void {
  store.set({ ...initialState });
}

export default itemStore;
